import React from 'react';
import { Button, Dialog, DialogTitle, MenuItem } from '@mui/material';
import { DateTimeInput, SimpleForm, Toolbar, useRecordContext } from 'react-admin';
import useDialog from 'hooks/useDialog';
import { ApplicationStatus, ApprovedStatus } from '../ApplicationStatusControl';
import { useFormContext } from 'react-hook-form';
import ScreenerAccountInput from 'components/Filter/ScreenerAccountInput';

type ApproveFormValues = {
  screener_account_id: number | null;
  scheduled_time: string | null;
};

type ProfessionalScreening = {
  id: number;
  screening_type: 'phone' | 'written';
  canceled_at: string | null;
  reason: 'application' | 're_screening';
  no_show: boolean;
  scheduled_time: string | null;
  screener_account_id: number | null;
};

type ApprovePayload = {
  application_status: typeof ApprovedStatus;
  professional_id?: number;
  screener_account_id?: number;
  scheduled_time?: string;
  reason?: string;
  screening_type?: 'phone' | 'written';
};

enum ApproveLabel {
  resume_review = 'Approve (Written Screen)',
  phone_screen_pending = 'Approve (Written Screen)',
}
enum ApproveConfirmationLabel {
  resume_review = 'Approve as Written Screen?',
  phone_screen_pending = 'Approve as Written Screen?',
}

interface ApproveItemProps {
  currentApplicationStatus: ApplicationStatus;
  updateApplicationStatus: (val: ApprovePayload) => void;
}

const ApproveItem = ({ currentApplicationStatus, updateApplicationStatus }: ApproveItemProps) => {
  const { open, closeDialog, openDialog } = useDialog();
  return (
    <>
      <MenuItem onClick={openDialog}>
        {ApproveLabel[currentApplicationStatus] ?? 'Approve'}
      </MenuItem>
      <ApproveDialog
        open={open}
        closeDialog={closeDialog}
        updateApplicationStatus={updateApplicationStatus}
        currentApplicationStatus={currentApplicationStatus}
      />
    </>
  );
};

const ApproveToolBar = ({ closeDialog }) => {
  const { getValues } = useFormContext();
  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button onClick={closeDialog}>Cancel</Button>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!getValues().screener_account_id || !getValues().scheduled_time}
      >
        Submit
      </Button>
    </Toolbar>
  );
};

const ApproveDialog = ({
  open,
  closeDialog,
  updateApplicationStatus,
  currentApplicationStatus,
}) => {
  const record = useRecordContext();

  const latestScreening = record?.professional_screenings?.find(
    (screening: ProfessionalScreening) =>
      screening?.screening_type === 'phone' &&
      !screening?.canceled_at &&
      !screening?.no_show &&
      screening?.scheduled_time,
  );

  const defaultValues = {
    screener_account_id: latestScreening?.screener_account_id || null,
    scheduled_time: latestScreening?.scheduled_time || null,
  };

  const submitApprove = ({ screener_account_id, scheduled_time }: ApproveFormValues) => {
    const hasScreeningChanges =
      screener_account_id !== defaultValues.screener_account_id ||
      scheduled_time !== defaultValues.scheduled_time;

    const payload: ApprovePayload = {
      application_status: ApprovedStatus,
    };

    if (hasScreeningChanges) {
      payload.professional_id = Number(record?.id);
      payload.screener_account_id = screener_account_id ?? undefined;
      payload.scheduled_time = scheduled_time ?? undefined;
      payload.reason = 'application';
      payload.screening_type = latestScreening?.screening_type || 'written';
    }

    updateApplicationStatus(payload);
  };

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>
        {ApproveConfirmationLabel[currentApplicationStatus] ?? 'Approve application?'}
      </DialogTitle>
      <SimpleForm
        key={open ? 'open' : 'closed'}
        onSubmit={submitApprove}
        toolbar={<ApproveToolBar closeDialog={closeDialog} />}
        defaultValues={defaultValues}
      >
        <ScreenerAccountInput
          source="screener_account_id"
          label="Screening Recruiter"
          alwaysOn
          fullWidth
        />
        <DateTimeInput label="Screening Date And Time" source="scheduled_time" fullWidth />
      </SimpleForm>
    </Dialog>
  );
};

export default ApproveItem;
